import type { CollectionModel } from '@ikegps/core-shared';
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import type { BaseReportComponent } from '../components/BaseReportComponent';
import { pdfTheme } from '../pdfTheme';

/*
Note:
The pdfmake library has limited styling functionality and is still missing some fairly important features.
As of the current version (v0.2.9), the library does not support vertically aligned text out of the box.
There is a work around if feature this is necessary in the future, but has not been included here.
You can read more about it here: https://github.com/bpampuch/pdfmake/issues/74#issuecomment-1872332430
*/

/**
 * The base class for all reports using the PDFMake library
 * Documentation can be found here: https://pdfmake.github.io/docs/0.1/
 */
export abstract class ReportBase {
	/**
	 * The poles to be included in the report
	 */
	poles: CollectionModel[] = [];
	images: Record<string, string> = {};
	nodes: BaseReportComponent[] = [];

	constructor(poles: CollectionModel[]) {
		this.poles = poles;
		this.buildPDF();
	}

	/**
	 * Create the nodes of the PDF to be placed in the `this.nodes` array
	 */
	abstract buildPDF(): void;

	/**
	 * Export the PDF to be used by the PDFMake library
	 */
	export(): TDocumentDefinitions {
		const nodeTypeStyles: BaseReportComponent[] = [];
		const nodeTypePatterns: BaseReportComponent[] = [];
		const nodeContent: Content[] = [];

		// Fetch the styles of each node, and the nodes that they use
		const fetchStyles = (node: BaseReportComponent) => {
			node.nodes?.forEach((deeperNode) => fetchStyles(deeperNode));

			if (node.exportStyles && !nodeTypeStyles.some((x) => x.constructor.name === node.constructor.name)) {
				nodeTypeStyles.push(node);
			}
		};

		// Fetch the patterns of each node, and the nodes that they use
		const fetchPatterns = (node: BaseReportComponent) => {
			node.nodes?.forEach((deeperNode) => fetchPatterns(deeperNode));

			if (node.exportPatterns && !nodeTypePatterns.some((x) => x.constructor.name === node.constructor.name)) {
				nodeTypePatterns.push(node);
			}
		};

		// Fetch the styles and content of each node
		this.nodes.forEach((node) => {
			fetchStyles(node);
			fetchPatterns(node);
			nodeContent.push(node.exportContent());
		});

		return {
			pageSize: pdfTheme.page.size,
			// setting pageMargins to '0' causes issues because pdfmake does falsey checks on its value
			// so set it to an array of 0s instead
			pageMargins: [0, 0],
			content: nodeContent,
			images: this.images,
			styles: nodeTypeStyles.reduce((styles, node) => ({ ...styles, ...node.exportStyles() }), {}),
			patterns: nodeTypePatterns.reduce((patterns, node) => ({ ...patterns, ...node.exportPatterns() }), {}),
		};
	}
}
